import CustomTabPanel from "components/Shared/CustomTabPanel";
import CustomTabs from "components/Shared/CustomTabs";
import { useBranchAssignmentContext } from "contexts/BranchAssignmentDetailContext";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import BranchAssignmentRegionTab from "./BranchAssignmentRegionTab";
import BranchAssignmentDistrictTab from "./BranchAssignmentDistrictTab";
import BranchAssignmentMunicipalityTab from "./BranchAssignmentMunicipalityTab";
import BranchAssignmentBranchTab from "./BranchAssignmentBranchTab";

const BranchAssignmentDetailTabs = () => {
  const { branchAssignmentDetail, loadingBranchAssignmentDetail } =
    useBranchAssignmentContext();

  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");

  const [activeTab, setActiveTab] = useState(tabFromUrl || "region");

  // Keep local activeTab in sync with the URL `tab` param so deep links
  // (e.g. ?tab=assignedBranches) actually switch panels.
  React.useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Default to the primary tab for the assignment when no `tab` is in the URL.
  React.useEffect(() => {
    if (tabFromUrl) return;
    if (branchAssignmentDetail?.assignmentType === "Region") {
      setActiveTab("region");
    } else if (branchAssignmentDetail?.assignmentType === "District") {
      setActiveTab("district");
    } else if (branchAssignmentDetail?.assignmentType === "Municipality") {
      setActiveTab("municipalities");
    } else if (branchAssignmentDetail?.hasCustomAssignment === true) {
      setActiveTab("branch");
    }
  }, [branchAssignmentDetail, tabFromUrl]);

  const renderTabs = () => {
    const assignedBranches = branchAssignmentDetail?.assignedBranches ?? [];

    const tabs = [];
    if (branchAssignmentDetail?.assignmentType === "Region") {
      tabs.push({
        label: "Assigned Province",
        value: "region",
      });
    }

    if (branchAssignmentDetail?.assignmentType === "District") {
      tabs.push({
        label: "Assigned District",
        value: "district",
      });
    }

    if (branchAssignmentDetail?.assignmentType === "Municipality") {
      tabs.push({
        label: "Assigned Municipalities",
        value: "municipalities",
      });
    }

    if (branchAssignmentDetail?.hasCustomAssignment === true) {
      tabs.push({
        label: "Assigned Branch",
        value: "branch",
      });
    }

    if (
      !branchAssignmentDetail?.hasCustomAssignment &&
      assignedBranches.length > 0
    ) {
      tabs.push({
        label: "Assigned Branches",
        value: "assignedBranches",
      });
    }
    return tabs;
  };

  const renderFilteredData = () => {
    const assignments = branchAssignmentDetail?.assignments ?? [];
    const customBranches = branchAssignmentDetail?.customBranches ?? [];
    const assignedBranches = branchAssignmentDetail?.assignedBranches ?? [];

    if (branchAssignmentDetail?.assignmentType === "Region") {
      return {
        region: assignments.map((region) => ({
          label: region?.name,
          id: region?._id,
        })),
        assignedBranches: assignedBranches.map((branch) => ({
          label: branch?.name,
          id: branch?._id,
        })),
      };
    }

    if (branchAssignmentDetail?.assignmentType === "District") {
      return {
        district: assignments.map((district) => ({
          label: district?.name,
          id: district?._id,
        })),
        assignedBranches: assignedBranches.map((branch) => ({
          label: branch?.name,
          id: branch?._id,
        })),
      };
    }
    if (branchAssignmentDetail?.hasCustomAssignment === true) {
      return {
        branch: customBranches.map((branch) => ({
          label: branch?.name,
          id: branch?._id,
        })),
      };
    }
    if (branchAssignmentDetail?.assignmentType === "Municipality") {
      return {
        muncipality: assignments.map((muncipality) => ({
          label: muncipality?.name,
          id: muncipality?._id,
        })),
        assignedBranches: assignedBranches.map((branch) => ({
          label: branch?.name,
          id: branch?._id,
        })),
      };
    }
  };

  const tabs = renderTabs();
  const filteredData = renderFilteredData();

  return (
    <>
      <CustomTabs tabs={tabs} activeTab={activeTab} />
      {!loadingBranchAssignmentDetail ? (
        <>
          {tabs.some((tab) => tab.value === "region") && (
            <CustomTabPanel activeTab={activeTab} value="region">
              <BranchAssignmentRegionTab regionDetail={filteredData.region} />
            </CustomTabPanel>
          )}
          {tabs.some((tab) => tab.value === "district") && (
            <CustomTabPanel activeTab={activeTab} value="district">
              <BranchAssignmentDistrictTab
                districtsDetail={filteredData.district}
              />
            </CustomTabPanel>
          )}
          {tabs.some((tab) => tab.value === "municipalities") && (
            <CustomTabPanel activeTab={activeTab} value="municipalities">
              <BranchAssignmentMunicipalityTab
                municipalityDetail={filteredData.muncipality}
              />
            </CustomTabPanel>
          )}

          {tabs.some((tab) => tab.value === "branch") && (
            <CustomTabPanel activeTab={activeTab} value="branch">
              <BranchAssignmentBranchTab branchDetail={filteredData.branch} />
            </CustomTabPanel>
          )}

          {tabs.some((tab) => tab.value === "assignedBranches") && (
            <CustomTabPanel
              activeTab={activeTab}
              value="assignedBranches"
            >
              <BranchAssignmentBranchTab
                branchDetail={filteredData.assignedBranches}
              />
            </CustomTabPanel>
          )}
        </>
      ) : (
        <RowsSkeleton />
      )}
    </>
  );
};

export default BranchAssignmentDetailTabs;
